import * as React from 'react';
import _ from 'lodash';
import { styled } from '@mui/material/styles';
import i18n, { translateBlockValueWithLabel } from 'i18n';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SelectChevron from 'components/inputs/SelectChevron';
import { sortTreeByChildren } from 'helpers/treeHelpers';
import AdornedInput from 'components/inputs/AdornedInput/AdornedInput';
import { SelectionTreeT, ScopeOptionsT } from 'business/definitions';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import TreeModalPicker from './TreeModalPicker';
import { UNMAPPED } from 'business/constants';

type Props = {
    isLoading: boolean;
    onChange: (arg1: SelectionTreeT[]) => void;
    gridOptions: TreeT;
    selectedValues: SelectionTreeT[];
    title: string;
    scopeOptions: ScopeOptionsT[];
    errorText?: string;
    isChip?: boolean;
    onDelete?: () => void;
    inputProps?: Record<string, unknown>;
    placeholder?: string; // Only used for queryPerimeter filter
    inputLabel?: string;
    variant?: TextFieldProps['variant'];
    required?: boolean;
    disabled?: boolean;
    isValueLocked?: boolean;
    className?: string;
    onClick?: (arg1: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    doNotSort?: boolean;
    'data-testid'?: string;
    showFilterPrefixOnDialogTitle?: boolean;
};

// TODO: it would be better to rewrite the component and not use a TextField since we don't
// write anything
const StyTextField = styled(TextField, {
    name: 'StyTextField',
})(({ placeholder }) => ({
    '& .MuiInputBase-root': {
        fontSize: '14px',
        cursor: 'pointer',
        '& input': {
            // It reduces de size of the filters depending on the length of the placeholder
            // To avoid really big filters
            maxWidth: placeholder
                ? `${Math.ceil(placeholder.length * 0.9)}ch`
                : 0,
            // Hides the blinking cursor
            caretColor: 'transparent',
            cursor: 'pointer',
        },
    },
}));

function labelUnmappedDestinations(unTranslatedTree: TreeT) {
    //Translate any children with "unmapped" value by adding a label to it
    if (
        unTranslatedTree.children.some(
            (children: TreeT) => children.value === 'unmapped',
        )
    ) {
        const newTreeChildren = unTranslatedTree.children.map(
            (children: TreeT) => {
                if (children.value === 'unmapped') {
                    return {
                        ...children,
                        label: i18n.t(UNMAPPED),
                    };
                }
                return children;
            },
        );
        return { ...unTranslatedTree, children: newTreeChildren };
    }
    return unTranslatedTree;
}

const TreeTablePicker = ({
    selectedValues,
    isLoading,
    gridOptions,
    onChange,
    title,
    errorText = undefined,
    scopeOptions,
    'data-testid': dataTestId = undefined,
    inputLabel = '',
    showFilterPrefixOnDialogTitle = true,
    placeholder = '',
    className = '',
    variant = 'standard',
    disabled = false,
    isValueLocked = false,
    onClick: onClickProp = () => {},
    required = false,
    isChip = false,
    onDelete = () => {},
    doNotSort = false,
    inputProps = {},
}: Props) => {
    const { t } = useTranslation();

    const [isModalOpen, setModalOpen] = React.useState(false);

    const close = () => setModalOpen(false);
    const open = () => !disabled && !isValueLocked && setModalOpen(true);
    const onClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
        open();
        if (typeof onClickProp === 'function') onClickProp(event);
    };
    const submit = (val: SelectionTreeT[]) => {
        onChange(val);
        close();
    };
    const onRemoveClick = (nodeToRemove: SelectionTreeT): void => {
        onChange(
            selectedValues.filter((node) => node.value !== nodeToRemove.value),
        );
    };

    const valuesChips = AdornedInput(
        selectedValues.map(translateBlockValueWithLabel),
        onRemoveClick,
        !!isValueLocked,
    );

    if (isLoading) {
        return (
            <>
                <StyTextField
                    className={className}
                    disabled
                    variant={variant}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SelectChevron />
                            </InputAdornment>
                        ),
                    }}
                />
                <Dialog open={isModalOpen} onClose={close} maxWidth="md">
                    <DialogTitle>{title}</DialogTitle>
                    <DialogContent>
                        <CircularProgress />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close}>{t('common.cancel')}</Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    const placeHolderText =
        _.isEmpty(selectedValues) || selectedValues[0].value === ''
            ? placeholder
            : null;

    const chipLabel = !_.isEmpty(selectedValues)
        ? selectedValues.map((val) => val.label).join(', ')
        : '...';

    const tree =
        doNotSort || gridOptions === undefined
            ? gridOptions
            : sortTreeByChildren(gridOptions);

    return (
        <>
            {!isChip ? (
                <StyTextField
                    onClick={onClick}
                    data-testid={dataTestId}
                    error={!!errorText}
                    helperText={errorText}
                    InputProps={
                        inputProps
                            ? { ...valuesChips, ...inputProps }
                            : valuesChips
                    }
                    placeholder={placeHolderText || ''}
                    variant={variant}
                    required={!!required}
                    disabled={!!disabled}
                    className={className}
                    label={inputLabel}
                    InputLabelProps={{ style: { paddingRight: '25px' } }}
                />
            ) : (
                <Tooltip title={chipLabel}>
                    <Chip
                        variant="outlined"
                        className="text-underlined"
                        label={chipLabel}
                        onClick={onClick}
                        onDelete={onDelete}
                    />
                </Tooltip>
            )}

            {tree && (
                <TreeModalPicker
                    title={title}
                    show={isModalOpen}
                    onClose={close}
                    tree={labelUnmappedDestinations(tree)}
                    defaultSelection={selectedValues}
                    submit={submit}
                    scopeOptions={scopeOptions}
                    showFilterPrefixOnDialogTitle={
                        showFilterPrefixOnDialogTitle
                    }
                />
            )}
        </>
    );
};

export default TreeTablePicker;
